import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CheckCircle2, XCircle, Eye, EyeOff, Building2, MapPin, ExternalLink, FileSearch, Star, MoreVertical, Send, MessageSquare, RotateCcw, Search
} from 'lucide-react';
import { BulkActionBar } from './BulkActionBar';
import { logAdminAction } from '@/lib/auditLog';
import { resolveProjectSubmitterUserId } from '@/lib/projectOwnership';
import { PackageQuotaBadge } from './PackageQuotaBadge';

const statusConfig: Record<string, { label: string; color: string }> = {
  draft: { label: 'Draft', color: 'bg-gray-100 text-gray-700' },
  pending_review: { label: 'Pending Review', color: 'bg-yellow-100 text-yellow-800' },
  approved: { label: 'Approved', color: 'bg-green-100 text-green-800' },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-800' },
};

const actionConfig: Record<string, { label: string; dot: string; icon: any }> = {
  feedback_sent: { label: 'Feedback sent', dot: 'bg-amber-500', icon: MessageSquare },
  resubmitted: { label: 'Resubmitted by developer', dot: 'bg-blue-500', icon: RotateCcw },
  approved: { label: 'Approved', dot: 'bg-green-500', icon: CheckCircle2 },
  rejected: { label: 'Rejected', dot: 'bg-red-500', icon: XCircle },
};

const getName = (val: any) => typeof val === 'object' ? (val?.en || '') : (val || '');

export function ProjectApprovals() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [selectedProject, setSelectedProject] = useState<any>(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [markFeatured, setMarkFeatured] = useState(false);
  const [filterTab, setFilterTab] = useState('pending_review');
  const [submitterFilter, setSubmitterFilter] = useState<'all' | 'developer' | 'agency'>('all');
  const [agencySearch, setAgencySearch] = useState('');
  const [sortBy, setSortBy] = useState<'newest' | 'oldest' | 'pending_edits' | 'name_asc'>('newest');

  const { data: projects, isLoading } = useQuery({
    queryKey: ['projects-for-approval'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('projects')
        .select('*, developer_profiles(user_id, company_name, logo_url), marketing_agency:agencies!projects_marketing_agency_id_fkey(id, name, logo_url, created_by), project_images(url, is_primary)')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data || [];
    },
  });

  // Review history for the currently-open project
  const { data: history } = useQuery({
    queryKey: ['project-review-history', selectedProject?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('project_review_history' as any)
        .select('*')
        .eq('project_id', selectedProject!.id)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data || [];
    },
    enabled: !!selectedProject,
  });

  const resolveDeveloperUserId = async (project: any): Promise<string | null> => {
    // Prefer joined fields (handles both developer-submitted and agency-submitted projects)
    const fromJoin = resolveProjectSubmitterUserId(project);
    if (fromJoin) return fromJoin;
    // Fallback: hydrate developer_profile if only the id is present
    if (project?.developer_id) {
      const { data: dp } = await supabase
        .from('developer_profiles')
        .select('user_id')
        .eq('id', project.developer_id)
        .single();
      if (dp?.user_id) return dp.user_id;
    }
    // Fallback: hydrate agency.created_by if only the id is present
    if (project?.marketing_agency_id) {
      const { data: ag } = await supabase
        .from('agencies')
        .select('created_by')
        .eq('id', project.marketing_agency_id)
        .maybeSingle();
      if (ag?.created_by) return ag.created_by;
    }
    return null;
  };

  const writeHistory = async (projectId: string, action: string, notes: string) => {
    if (!user) return;
    await supabase.from('project_review_history' as any).insert({
      project_id: projectId,
      reviewer_id: user.id,
      action,
      notes: notes || null,
    });
  };

  // Apply pending_changes to live columns + child collections, then clear pending fields
  const mergePendingChanges = async (projectId: string, pending: any) => {
    if (!pending) return;
    const liveUpdate: any = {};
    const liveColumns = ['name', 'description', 'city', 'sub_city', 'address', 'status', 'currency', 'price_min', 'price_max', 'total_units', 'available_units', 'completion_date', 'latitude', 'longitude'];
    liveColumns.forEach(col => { if (pending[col] !== undefined) liveUpdate[col] = pending[col]; });
    if (Object.keys(liveUpdate).length > 0) {
      await supabase.from('projects').update(liveUpdate).eq('id', projectId);
    }

    // Replace child collections if snapshot present
    if (Array.isArray(pending._images)) {
      await supabase.from('project_images').delete().eq('project_id', projectId);
      if (pending._images.length > 0) {
        await supabase.from('project_images').insert(
          pending._images.map((img: any, i: number) => ({ project_id: projectId, url: img.url, is_floor_plan: !!img.is_floor_plan, is_primary: i === 0, sort_order: i }))
        );
      }
    }
    if (Array.isArray(pending._unit_types)) {
      await supabase.from('project_unit_types').delete().eq('project_id', projectId);
      if (pending._unit_types.length > 0) {
        await supabase.from('project_unit_types').insert(
          pending._unit_types.map((u: any, i: number) => ({
            project_id: projectId, name: u.name, sort_order: i,
            area_min: u.area_min ? Number(u.area_min) : null,
            area_max: u.area_max ? Number(u.area_max) : null,
            bedrooms: u.bedrooms ? Number(u.bedrooms) : null,
            bathrooms: u.bathrooms ? Number(u.bathrooms) : null,
            price_min: u.price_min ? Number(u.price_min) : null,
            price_max: u.price_max ? Number(u.price_max) : null,
            currency: u.currency || 'ETB',
            floor_plan_urls: Array.isArray(u.floor_plan_urls)
              ? u.floor_plan_urls
              : (u.floor_plan_url ? [u.floor_plan_url] : []),
          } as any))
        );
      }
    }
    if (Array.isArray(pending._features)) {
      await supabase.from('project_features').delete().eq('project_id', projectId);
      if (pending._features.length > 0) {
        await supabase.from('project_features').insert(
          pending._features.map((f: any, i: number) => ({ project_id: projectId, category: f.category, icon: f.icon, items: f.items, sort_order: i }))
        );
      }
    }
    if (Array.isArray(pending._updates)) {
      await supabase.from('project_updates').delete().eq('project_id', projectId);
      if (pending._updates.length > 0) {
        await supabase.from('project_updates').insert(
          pending._updates.map((u: any, i: number) => ({
            project_id: projectId, title: u.title, description: u.description || null,
            update_date: u.update_date, status: u.status, image_url: u.image_url || null, sort_order: i,
          }))
        );
      }
    }
    if (Array.isArray(pending._payment_plan_images)) {
      await supabase.from('project_payment_plan_images' as any).delete().eq('project_id', projectId);
      if (pending._payment_plan_images.length > 0) {
        await supabase.from('project_payment_plan_images' as any).insert(
          pending._payment_plan_images.map((p: any, i: number) => ({
            project_id: projectId, url: p.url, title: p.title || null, sort_order: i,
          }))
        );
      }
    }
  };

  const reviewMutation = useMutation({
    mutationFn: async ({ projectId, status, notes, featured, project }: { projectId: string; status: string; notes: string; featured?: boolean; project: any }) => {
      const hasPending = !!project.has_pending_changes;
      const isApprovedWithEdits = project.approval_status === 'approved' && hasPending;

      // For approved projects with pending edits, approve = merge edits, reject = discard edits
      if (isApprovedWithEdits && status === 'approved') {
        await mergePendingChanges(projectId, project.pending_changes);
        const update: any = { reviewer_notes: notes, has_pending_changes: false, pending_changes: null };
        if (featured) update.is_featured = true;
        const { error } = await supabase.from('projects').update(update).eq('id', projectId);
        if (error) throw error;
      } else if (isApprovedWithEdits && status === 'rejected') {
        // Discard pending edits, keep project approved & live
        const { error } = await supabase
          .from('projects')
          .update({ reviewer_notes: notes, has_pending_changes: false, pending_changes: null })
          .eq('id', projectId);
        if (error) throw error;
      } else {
        // Standard approve/reject for pending_review
        const update: any = { approval_status: status, reviewer_notes: notes };
        if (featured) update.is_featured = true;
        const { error } = await supabase.from('projects').update(update).eq('id', projectId);
        if (error) throw error;
      }

      await writeHistory(projectId, status, notes);

      const recipientId = await resolveDeveloperUserId(project);
      if (recipientId) {
        const projName = getName(project.name);
        const truncated = notes ? (notes.length > 200 ? notes.slice(0, 200) + '…' : notes) : '';
        const title = isApprovedWithEdits
          ? (status === 'approved' ? `Edits approved: ${projName}` : `Edits rejected: ${projName}`)
          : (status === 'approved' ? `Project approved: ${projName}` : `Project rejected: ${projName}`);
        const message = truncated || (
          isApprovedWithEdits
            ? (status === 'approved' ? 'Your edits have been approved and are now live.' : 'Your edits were rejected. The previous live version remains visible.')
            : (status === 'approved' ? 'Your project has been approved and is now live.' : 'Your project was rejected. Please review the feedback.')
        );
        await supabase.from('notifications').insert({
          user_id: recipientId,
          type: 'project_reviewed',
          title,
          message,
          metadata: { project_id: projectId, status, notes, was_pending_edits: isApprovedWithEdits },
        });
      }
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ['projects-for-approval'] });
      queryClient.invalidateQueries({ queryKey: ['project-review-history'] });
      toast.success(vars.status === 'approved' ? 'Approved! Developer notified.' : 'Rejected. Developer notified.');
      setSelectedProject(null);
      setReviewNotes('');
      setMarkFeatured(false);
    },
    onError: (err: any) => toast.error(err.message),
  });

  // Send feedback WITHOUT changing approval status — keeps project in pending_review
  const sendFeedbackMutation = useMutation({
    mutationFn: async ({ projectId, notes, project }: { projectId: string; notes: string; project: any }) => {
      const { error } = await supabase.from('projects').update({ reviewer_notes: notes }).eq('id', projectId);
      if (error) throw error;

      await writeHistory(projectId, 'feedback_sent', notes);

      const recipientId = await resolveDeveloperUserId(project);
      if (recipientId) {
        const projName = getName(project.name);
        const truncated = notes.length > 200 ? notes.slice(0, 200) + '…' : notes;
        await supabase.from('notifications').insert({
          user_id: recipientId,
          type: 'project_review_feedback',
          title: `QC sent feedback on: ${projName}`,
          message: truncated,
          metadata: { project_id: projectId, action: 'feedback_sent', notes },
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects-for-approval'] });
      queryClient.invalidateQueries({ queryKey: ['project-review-history'] });
      toast.success('Feedback sent to developer.');
    },
    onError: (err: any) => toast.error(err.message),
  });

  const quickActionMutation = useMutation({
    mutationFn: async ({ projectId, updates }: { projectId: string; updates: Record<string, any> }) => {
      const { error } = await supabase.from('projects').update(updates as any).eq('id', projectId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects-for-approval'] });
      toast.success('Project updated');
    },
    onError: (err: any) => toast.error(err.message),
  });

  const filtered = projects?.filter((p: any) => {
    // Status tab
    if (filterTab !== 'all') {
      if (filterTab === 'pending_review') {
        if (!(p.approval_status === 'pending_review' || p.has_pending_changes)) return false;
      } else if (p.approval_status !== filterTab) return false;
    }
    // Submitter type
    if (submitterFilter === 'developer' && !p.developer_id) return false;
    if (submitterFilter === 'agency' && !p.marketing_agency_id) return false;
    // Marketed-by agency name search
    const q = agencySearch.trim().toLowerCase();
    if (q && !(p.marketing_agency?.name || '').toLowerCase().includes(q)) return false;
    return true;
  }) || [];

  const sorted = [...filtered].sort((a: any, b: any) => {
    if (sortBy === 'pending_edits') {
      const ap = a.has_pending_changes ? 0 : 1;
      const bp = b.has_pending_changes ? 0 : 1;
      if (ap !== bp) return ap - bp;
      return new Date(b.updated_at || b.created_at).getTime() - new Date(a.updated_at || a.created_at).getTime();
    }
    if (sortBy === 'oldest') {
      return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
    }
    if (sortBy === 'name_asc') {
      return getName(a.name).localeCompare(getName(b.name));
    }
    // newest
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  });

  const pendingCount = projects?.filter((p: any) => p.approval_status === 'pending_review' || p.has_pending_changes).length || 0;
  const developerCount = projects?.filter((p: any) => p.developer_id).length || 0;
  const agencyCount = projects?.filter((p: any) => p.marketing_agency_id).length || 0;

  if (isLoading) return <div className="space-y-4">{[1, 2, 3].map(i => <Skeleton key={i} className="h-20" />)}</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-xl font-bold">Project Management</h2>
        {pendingCount > 0 && <Badge className="bg-yellow-500 text-white">{pendingCount} Pending</Badge>}
      </div>

      <Tabs value={filterTab} onValueChange={setFilterTab}>
        <TabsList>
          <TabsTrigger value="all">All ({projects?.length || 0})</TabsTrigger>
          <TabsTrigger value="pending_review">Pending ({pendingCount})</TabsTrigger>
          <TabsTrigger value="approved">Approved ({projects?.filter((p: any) => p.approval_status === 'approved').length || 0})</TabsTrigger>
          <TabsTrigger value="rejected">Rejected ({projects?.filter((p: any) => p.approval_status === 'rejected').length || 0})</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
        <Tabs value={submitterFilter} onValueChange={(v) => setSubmitterFilter(v as any)}>
          <TabsList>
            <TabsTrigger value="all">All Submitters ({projects?.length || 0})</TabsTrigger>
            <TabsTrigger value="developer">Developer ({developerCount})</TabsTrigger>
            <TabsTrigger value="agency">Agency ({agencyCount})</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="relative flex-1 max-w-xs">
          <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={agencySearch}
            onChange={(e) => setAgencySearch(e.target.value)}
            placeholder="Search by marketed-by agency…"
            className="pl-8 h-9"
          />
        </div>
        <Select value={sortBy} onValueChange={(v) => setSortBy(v as any)}>
          <SelectTrigger className="h-9 w-[180px]"><SelectValue placeholder="Sort by" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="newest">Newest first</SelectItem>
            <SelectItem value="oldest">Oldest first</SelectItem>
            <SelectItem value="pending_edits">Pending edits first</SelectItem>
            <SelectItem value="name_asc">Name (A–Z)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {!sorted.length ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground">
          <FileSearch className="h-12 w-12 mx-auto mb-3 opacity-50" /><p>No projects in this category</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-3">
          {sorted.map((project: any) => {
            const name = getName(project.name);
            const cfg = statusConfig[project.approval_status] || statusConfig.draft;
            const img = project.project_images?.find((i: any) => i.is_primary)?.url || project.project_images?.[0]?.url;
            return (
              <Card key={project.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <div className="h-14 w-14 rounded-lg bg-muted overflow-hidden shrink-0">
                      {img ? <img src={img} alt={name} className="h-full w-full object-cover" /> : <Building2 className="h-6 w-6 text-muted-foreground m-auto mt-4" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">{name}</span>
                        {project.is_featured && <Star className="h-3.5 w-3.5 text-yellow-500 fill-yellow-500" />}
                      </div>
                      <div className="text-sm text-muted-foreground flex items-center gap-1 flex-wrap">
                        <MapPin className="h-3 w-3" /> {project.city}
                        {project.developer_profiles && <span> • By {project.developer_profiles.company_name}</span>}
                        {project.marketing_agency && (
                          <span className="inline-flex items-center gap-1">
                            •
                            <Badge variant="outline" className="border-purple-300 text-purple-700 bg-purple-50 text-[10px] px-1.5 py-0 inline-flex items-center gap-1">
                              {project.marketing_agency.logo_url ? (
                                <img src={project.marketing_agency.logo_url} alt="" className="h-3.5 w-3.5 rounded-sm object-cover" />
                              ) : (
                                <Building2 className="h-3 w-3" />
                              )}
                              Marketed by {project.marketing_agency.name}
                            </Badge>
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {project.total_units || 0} units • {project.status}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <Badge className={cfg.color}>{cfg.label}</Badge>
                      {project.has_pending_changes && (
                        <Badge variant="outline" className="border-blue-400 text-blue-700 bg-blue-50 text-[10px]">
                          Pending Edits
                        </Badge>
                      )}
                    </div>
                    <div className="flex gap-1">
                      <Link to={`/projects/${project.id}`} target="_blank">
                        <Button variant="ghost" size="sm"><ExternalLink className="h-4 w-4" /></Button>
                      </Link>
                      <Button variant="outline" size="sm" onClick={() => {
                        setSelectedProject(project);
                        setReviewNotes(project.reviewer_notes || '');
                        setMarkFeatured(project.is_featured || false);
                      }}>
                        <Eye className="h-4 w-4 mr-1" /> Review
                      </Button>
                      {project.approval_status === 'approved' && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon"><MoreVertical className="h-4 w-4" /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => quickActionMutation.mutate({ projectId: project.id, updates: { is_featured: !project.is_featured } })}>
                              <Star className="h-4 w-4 mr-2" /> {project.is_featured ? 'Remove Featured' : 'Mark Featured'}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem className="text-destructive" onClick={() => quickActionMutation.mutate({ projectId: project.id, updates: { approval_status: 'rejected' } })}>
                              <EyeOff className="h-4 w-4 mr-2" /> Remove from Site
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Review Dialog */}
      <Dialog open={!!selectedProject} onOpenChange={() => setSelectedProject(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] flex flex-col p-0 gap-0">
          <DialogHeader className="px-6 pt-6 pb-3 border-b shrink-0">
            <DialogTitle>Review: {selectedProject && getName(selectedProject.name)}</DialogTitle>
          </DialogHeader>
          {selectedProject && (
            <>
              <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
                <div className="text-sm space-y-1.5 bg-muted/50 rounded-lg p-3">
                  <div><strong>City:</strong> {selectedProject.city}{selectedProject.sub_city ? `, ${selectedProject.sub_city}` : ''}</div>
                  {selectedProject.developer_profiles?.company_name && (
                    <div><strong>Developer:</strong> {selectedProject.developer_profiles.company_name}</div>
                  )}
                  {selectedProject.marketing_agency?.name && (
                    <div className="flex items-center gap-2">
                      <strong>Marketed by:</strong>
                      {selectedProject.marketing_agency.logo_url ? (
                        <img src={selectedProject.marketing_agency.logo_url} alt="" className="h-5 w-5 rounded object-cover border" />
                      ) : (
                        <Building2 className="h-4 w-4 text-muted-foreground" />
                      )}
                      <span>{selectedProject.marketing_agency.name}</span>
                    </div>
                  )}
                  {!selectedProject.developer_profiles?.company_name && !selectedProject.marketing_agency?.name && (
                    <div className="text-muted-foreground italic">Submitter info unavailable</div>
                  )}
                  <div><strong>Units:</strong> {selectedProject.total_units || 0} total, {selectedProject.available_units || 0} available</div>
                  <div><strong>Price Range:</strong> {selectedProject.price_min?.toLocaleString()} - {selectedProject.price_max?.toLocaleString()} {selectedProject.currency}</div>
                  <div><strong>Status:</strong> {selectedProject.status} • <strong>Approval:</strong> {selectedProject.approval_status}</div>
                  {selectedProject.completion_date && <div><strong>Completion:</strong> {selectedProject.completion_date}</div>}
                  <div className="pt-1">
                    <PackageQuotaBadge userId={selectedProject.created_by} kind="project" approvalStatus={selectedProject.approval_status} />
                  </div>
                </div>

                {selectedProject.project_images?.length > 0 && (
                  <div className="flex gap-2 overflow-x-auto pb-1">
                    {selectedProject.project_images.map((img: any, i: number) => (
                      <img key={i} src={img.url} alt="" className="h-16 w-20 rounded object-cover shrink-0" />
                    ))}
                  </div>
                )}

                {/* Pending edits diff */}
                {selectedProject.has_pending_changes && selectedProject.pending_changes && (() => {
                  const pc = selectedProject.pending_changes;
                  const diffFields = ['name', 'description', 'city', 'sub_city', 'address', 'status', 'currency', 'price_min', 'price_max', 'total_units', 'available_units', 'completion_date'];
                  const renderVal = (v: any) => {
                    if (v === null || v === undefined || v === '') return <em className="text-muted-foreground">empty</em>;
                    if (typeof v === 'object') return v.en || JSON.stringify(v);
                    return String(v);
                  };
                  const changes = diffFields
                    .map(f => ({ field: f, oldVal: (selectedProject as any)[f], newVal: pc[f] }))
                    .filter(c => c.newVal !== undefined && JSON.stringify(c.oldVal) !== JSON.stringify(c.newVal));
                  return (
                    <div className="space-y-2 rounded-lg border-2 border-blue-300 bg-blue-50/50 p-3">
                      <div className="text-sm font-semibold flex items-center gap-1.5 text-blue-900">
                        <FileSearch className="h-4 w-4" /> Pending Edits Submitted by Developer
                      </div>
                      {changes.length === 0 ? (
                        <p className="text-xs text-muted-foreground">No field-level changes detected, but child collections (images, units, etc.) may have been updated.</p>
                      ) : (
                        <div className="space-y-1.5 text-xs">
                          {changes.map(c => (
                            <div key={c.field} className="grid grid-cols-[1fr_2fr] gap-2 items-start border-b border-blue-200/60 pb-1 last:border-0">
                              <span className="font-medium capitalize text-blue-900">{c.field.replace('_', ' ')}</span>
                              <div className="space-y-0.5">
                                <div className="text-red-700 line-through opacity-70">{renderVal(c.oldVal)}</div>
                                <div className="text-green-700 font-medium">{renderVal(c.newVal)}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      <p className="text-[11px] italic text-blue-800">
                        Approving will apply these edits to the live listing. Rejecting will discard them and keep the current live version.
                      </p>
                    </div>
                  );
                })()}

                <div className="space-y-1.5">
                  <Label htmlFor="review-notes" className="text-sm font-medium">Review Notes (visible to developer)</Label>
                  <Textarea
                    id="review-notes"
                    placeholder="Write feedback the developer will see..."
                    value={reviewNotes}
                    onChange={e => setReviewNotes(e.target.value)}
                    rows={4}
                  />
                </div>

                <div className="flex items-center gap-2">
                  <Switch checked={markFeatured} onCheckedChange={setMarkFeatured} id="proj-featured-toggle" />
                  <Label htmlFor="proj-featured-toggle" className="flex items-center gap-1.5">
                    <Star className="h-3.5 w-3.5" /> Mark as Featured Project
                  </Label>
                </div>

                {/* Review history timeline */}
                {history && history.length > 0 && (
                  <div className="space-y-2 pt-2 border-t">
                    <div className="text-sm font-semibold flex items-center gap-1.5">
                      <MessageSquare className="h-4 w-4" /> Review History ({history.length})
                    </div>
                    <div className="space-y-2.5">
                      {history.map((h: any) => {
                        const cfg = actionConfig[h.action] || actionConfig.feedback_sent;
                        const Icon = cfg.icon;
                        return (
                          <div key={h.id} className="flex gap-3 text-xs">
                            <div className="flex flex-col items-center">
                              <div className={`h-2.5 w-2.5 rounded-full ${cfg.dot} mt-1.5`} />
                            </div>
                            <div className="flex-1 bg-muted/40 rounded p-2">
                              <div className="flex items-center justify-between gap-2">
                                <span className="font-medium flex items-center gap-1">
                                  <Icon className="h-3 w-3" /> {cfg.label}
                                </span>
                                <span className="text-muted-foreground">{new Date(h.created_at).toLocaleString()}</span>
                              </div>
                              {h.notes && <p className="mt-1 whitespace-pre-wrap text-muted-foreground">{h.notes}</p>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              <DialogFooter className="gap-2 px-6 py-4 border-t shrink-0 bg-background flex-wrap">
                {(selectedProject.approval_status === 'pending_review' || selectedProject.has_pending_changes) ? (
                  <>
                    <Button
                      variant="destructive"
                      onClick={() => {
                        if (!reviewNotes.trim()) {
                          toast.error('Please provide review notes when rejecting.');
                          return;
                        }
                        reviewMutation.mutate({ projectId: selectedProject.id, status: 'rejected', notes: reviewNotes, project: selectedProject });
                      }}
                      disabled={reviewMutation.isPending || sendFeedbackMutation.isPending}
                    >
                      <XCircle className="h-4 w-4 mr-1" /> {selectedProject.has_pending_changes && selectedProject.approval_status === 'approved' ? 'Reject Edits' : 'Reject'}
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={() => {
                        if (!reviewNotes.trim()) {
                          toast.error('Please write feedback before sending.');
                          return;
                        }
                        sendFeedbackMutation.mutate({ projectId: selectedProject.id, notes: reviewNotes, project: selectedProject });
                      }}
                      disabled={sendFeedbackMutation.isPending || reviewMutation.isPending}
                    >
                      <Send className="h-4 w-4 mr-1" /> Send Review
                    </Button>
                    <Button
                      onClick={() => reviewMutation.mutate({ projectId: selectedProject.id, status: 'approved', notes: reviewNotes, featured: markFeatured, project: selectedProject })}
                      disabled={reviewMutation.isPending || sendFeedbackMutation.isPending}
                    >
                      <CheckCircle2 className="h-4 w-4 mr-1" /> {selectedProject.has_pending_changes && selectedProject.approval_status === 'approved' ? 'Approve Edits' : 'Approve'}
                    </Button>
                  </>
                ) : (
                  <Button onClick={() => {
                    quickActionMutation.mutate({ projectId: selectedProject.id, updates: { reviewer_notes: reviewNotes, is_featured: markFeatured } });
                    setSelectedProject(null);
                  }}>
                    Save Changes
                  </Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
